// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { mapMlBattery, type MlBattery } from '@/features/ml/mlBattery';

/** One job a worker is busy with. */
export type FleetJob = {
  id: string;
  /** course-analysis | course-data-acquire | flyover | ml | osm-extract. */
  kind: string;
  /** Course name, when the job kind is course-scoped. */
  courseName: string | null;
  /** Which job of the run this is (kind · provider · detector@model) for an analysis job, the source for an acquisition; null otherwise. */
  detail: string | null;
  /** Taken from its queue but not started — the worker's analyser is busy with another job. */
  waiting: boolean;
};

/** One batch-worker server as the fleet page shows it. */
export type FleetWorker = {
  workerId: string;
  host: string;
  version: string | null;
  capabilities: string[];
  /**
   * Every job the worker is busy with right now, oldest first — one per job kind at most,
   * since each kind is consumed on its own queue. Empty when idle. A list, not one job: a
   * machine training a model that also picked up a course analysis used to show "ML job"
   * alone, and the analysis read as never having started.
   */
  currentJobs: FleetJob[];
  lastSeenAt: string;
  online: boolean;
  /** When the worker process started, as it reports it. Null for a worker build that predates it. */
  startedAt: string | null;
  /** linux | windows | macos | unknown | null — null for a worker build that predates this field. */
  os: string | null;
  /** arm64 | x64 | ... . */
  architecture: string | null;
  /** CPUs the container can use (Docker-limit-aware, not necessarily the host's full count). */
  cpuCount: number | null;
  /** Memory the container can use, in bytes (a Docker memory limit if one is set, else the host's total). */
  totalMemoryBytes: number | null;
  /**
   * What this worker's ML training runner trains on — mps | cuda | cpu — or null when it has no
   * runner, could not reach it, or predates the field. Not the worker container's own hardware:
   * that is always a Linux VM with no accelerator, which is why a containerised runner reads cpu.
   */
  mlAccelerator: string | null;
  /**
   * The training runner's machine battery, and whether the runner is holding its job for it.
   * Null for a machine without a battery, or a runner that cannot say.
   */
  mlBattery: MlBattery | null;
  /**
   * The trained models this worker's analyser can run, by registry version. Every one of them
   * is run against a course after its classical analysis, so this says how many comparison
   * runs the machine contributes — empty means no ML analyser beside it, or a release too old
   * to report them.
   */
  modelVersions: string[];
};

/** All-time totals for one job kind. */
export type FleetKindStatistics = { total: number; succeeded: number; failed: number };

/** Fleet-wide numbers: how many servers, and how the three job kinds have done all-time. */
export type FleetStatistics = {
  workersTotal: number;
  workersOnline: number;
  courseAnalysis: FleetKindStatistics;
  flyover: FleetKindStatistics;
  ml: FleetKindStatistics;
  osmExtract: FleetKindStatistics;
};

/** How many jobs of each kind are waiting for a worker right now. */
export type FleetQueues = {
  courseAnalysisDispatched: number;
  flyoverQueued: number;
  mlQueued: number;
  osmExtractQueued: number;
  /** Analysis jobs planned or out on a worker, by job kind (detection, elevation, …). */
  analysisJobsOpen: Record<string, number>;
  /**
   * Every envelope filed in the batch exchange, by exchange kind (the command's routing key) —
   * claimed ones included. A claim does not remove the envelope: it stays under `queued/` until
   * the job is answered, so a worker that dies mid-run leaves the job to be picked up again
   * rather than losing it (see geodata's MlJobFilingWorker). `claimed` beside this is what says
   * how many are in a worker's hands right now.
   */
  queued: Record<string, number>;
  /** Claims workers hold right now — jobs in flight as the exchange sees them. */
  claimed: number;
  /**
   * Runs the database considers live. Acquisition envelopes belong to runs, so with none in
   * flight every queued acquisition is for a run that is over.
   */
  analysisRunsInFlight: number;
};

/** The exchange kinds that make up "course analysis" — one run fans out into all of these. */
export const ANALYSIS_EXCHANGE_KINDS = [
  'batch.course-analysis.run',
  'batch.course-analysis.run.model',
  'batch.course-analysis.elevation',
  'batch.course-analysis.canopy',
  'batch.course-analysis.imagery',
];

export const ACQUIRE_EXCHANGE_KIND = 'batch.course-data.acquire';

/**
 * The kind a worker reports holding while it acquires one source's data for a course. Not an
 * analysis job: it has no row of its own, so no counter the database keeps has it — see
 * `describeDivergence`.
 */
export const ACQUIRE_JOB_KIND = 'course-data-acquire';

/**
 * Every analysis job the database has open — planned and waiting for its data as well as
 * dispatched to a worker. The number the course-analysis tile headlines and the one its queue
 * window lists, so the two cannot disagree the way "1" over "5 job(s) waiting" did.
 */
export const countAnalysisJobsOpen = (queues: FleetQueues): number =>
  Object.values(queues.analysisJobsOpen).reduce((total, count) => total + count, 0);

/**
 * Where the exchange and the database disagree about what is waiting.
 *
 * The tiles count rows; the exchange line counts envelopes; a worker pulls envelopes. When rows
 * say 0 and envelopes say hundreds, the fleet is working through jobs the database has already
 * closed and dropping every answer — which is what happened on 2026-09-05, invisibly, for an
 * afternoon. The sweep withdraws such envelopes within a minute; this is how anyone finds out.
 */
export function describeDivergence(
  queues: FleetQueues,
  workers: FleetWorker[]
): {
  /** Analysis, flyover and ML envelopes beyond what the database has open. */
  closed: number;
  /** Acquisition envelopes with no run in flight to belong to. */
  orphanedAcquisitions: number;
  /** Jobs online workers report holding, acquisitions aside, beyond what the database has out on a worker — closed work still running. */
  closedInFlight: number;
} {
  const sum = (kinds: string[]) => kinds.reduce((total, kind) => total + (queues.queued[kind] ?? 0), 0);
  const closed =
    Math.max(0, sum(ANALYSIS_EXCHANGE_KINDS) - countAnalysisJobsOpen(queues)) +
    Math.max(0, (queues.queued['batch.flyover.render'] ?? 0) - queues.flyoverQueued) +
    Math.max(0, (queues.queued['batch.ml.run'] ?? 0) - queues.mlQueued);
  const acquisitions = queues.queued[ACQUIRE_EXCHANGE_KIND] ?? 0;
  const orphanedAcquisitions = queues.analysisRunsInFlight === 0 ? acquisitions : 0;
  // What the workers themselves say they hold, not the exchange's claims. A claim proves only
  // that a worker took the job once: a worker restarted mid-job leaves its claim standing for
  // the fifteen minutes of its lease, and on 2026-09-05 one such leftover had this line accuse
  // a worker whose card, right below it, said Idle. The heartbeat is the worker's own word.
  // Acquisitions are left out on both sides: an acquisition is a sweep's fan-out, one job per
  // source, with no row of its own — none of the counters below can hold it. Counted, two of
  // them held for one run with its one detection dispatched read as "one closed job still
  // running" (2026-09-20, run 50acf97d, Chalmers GK) when nothing was closed at all.
  const held = workers
    .filter((worker) => worker.online)
    .reduce((total, worker) => total + worker.currentJobs.filter((job) => job.kind !== ACQUIRE_JOB_KIND).length, 0);
  // Dispatched analyses and queued flyovers, ML runs and OSM extracts all keep their status
  // while a worker has them, so together they are what the database thinks is out on a worker.
  const closedInFlight = Math.max(
    0,
    held - (queues.courseAnalysisDispatched + queues.flyoverQueued + queues.mlQueued + queues.osmExtractQueued)
  );
  return { closed, orphanedAcquisitions, closedInFlight };
}

/** One job from any of the three kinds, already merged into one timeline. */
export type FleetActivityItem = {
  kind: 'course-analysis' | 'flyover' | 'ml' | 'osm-extract';
  id: string;
  status: string;
  at: string;
  finishedAt: string | null;
  workerId: string | null;
  summary: string | null;
  error: string | null;
  /** Course analyses: the run this job belongs to — what the steps panel opens. */
  runId: string | null;
  /** Course analyses: which job of the run this is (kind · provider · detector@model). */
  detail: string | null;
};

export type FleetSnapshot = {
  workers: FleetWorker[];
  queues: FleetQueues;
  statistics: FleetStatistics;
  activity: FleetActivityItem[];
};

export type FleetState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed' }
  | ({ status: 'ready'; loadedAt: number } & FleetSnapshot);

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const bool = (row: Row, ...keys: string[]): boolean => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'boolean') return value;
  }
  return false;
};

/** A `{ kind: count }` dictionary, keeping only the finite numbers. */
const counts = (value: unknown): Record<string, number> =>
  Object.fromEntries(
    Object.entries((value ?? {}) as Record<string, unknown>)
      .filter(([, count]) => typeof count === 'number' && Number.isFinite(count))
      .map(([kind, count]) => [kind, count as number]),
  );

const strArray = (row: Row, ...keys: string[]): string[] => {
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
};

/**
 * The worker's in-flight jobs. A geodata that predates the list reports one job in the three
 * `currentJob*` fields instead; that job is mapped as a one-element list so the card still
 * shows it.
 */
const jobs = (row: Row): FleetJob[] => {
  const rows = row.currentJobs ?? row.CurrentJobs;
  if (Array.isArray(rows)) {
    return rows
      .map((job) => {
        const r = (job ?? {}) as Row;
        const id = str(r, 'id', 'Id');
        const kind = str(r, 'kind', 'Kind');
        return id && kind
          ? {
              id,
              kind,
              courseName: str(r, 'courseName', 'CourseName'),
              detail: str(r, 'detail', 'Detail'),
              waiting: r.waiting === true || r.Waiting === true,
            }
          : null;
      })
      .filter((job): job is FleetJob => job !== null);
  }

  const id = str(row, 'currentJobId', 'CurrentJobId');
  const kind = str(row, 'currentJobKind', 'CurrentJobKind');
  return id && kind
    ? [{ id, kind, courseName: str(row, 'currentJobCourseName', 'CurrentJobCourseName'), detail: null, waiting: false }]
    : [];
};

const isFleetKind = (value: string | null): value is FleetActivityItem['kind'] =>
  value === 'course-analysis' || value === 'flyover' || value === 'ml';

/** Maps the BFF's `/api/geodata/batch/fleet` body — pulled out for direct unit testing. */
export const mapFleetResponse = (body: unknown): FleetSnapshot => {
  const root = (body ?? {}) as Row;

  const workerRows = (root.workers ?? root.Workers) as Row[] | undefined;
  const workers: FleetWorker[] = Array.isArray(workerRows)
    ? workerRows
        .map((row) => ({
          workerId: str(row, 'workerId', 'WorkerId') ?? '',
          host: str(row, 'host', 'Host') ?? '',
          version: str(row, 'version', 'Version'),
          capabilities: strArray(row, 'capabilities', 'Capabilities'),
          currentJobs: jobs(row),
          lastSeenAt: str(row, 'lastSeenAt', 'LastSeenAt') ?? '',
          online: bool(row, 'online', 'Online'),
          startedAt: str(row, 'startedAt', 'StartedAt'),
          os: str(row, 'os', 'Os'),
          architecture: str(row, 'architecture', 'Architecture'),
          cpuCount: numOrNull(row, 'cpuCount', 'CpuCount'),
          totalMemoryBytes: numOrNull(row, 'totalMemoryBytes', 'TotalMemoryBytes'),
          mlAccelerator: str(row, 'mlAccelerator', 'MlAccelerator'),
          mlBattery: mapMlBattery(row.mlBattery ?? row.MlBattery),
          modelVersions: strArray(row, 'modelVersions', 'ModelVersions'),
        }))
        .filter((worker) => worker.workerId.length > 0)
    : [];

  const queueRow = (root.queues ?? root.Queues ?? {}) as Row;
  const queues: FleetQueues = {
    courseAnalysisDispatched: num(queueRow, 'courseAnalysisDispatched', 'CourseAnalysisDispatched'),
    flyoverQueued: num(queueRow, 'flyoverQueued', 'FlyoverQueued'),
    mlQueued: num(queueRow, 'mlQueued', 'MlQueued'),
    // Absent from an older geodata, where num() reads it as zero — the right answer for a
    // service that files no extract jobs at all.
    osmExtractQueued: num(queueRow, 'osmExtractQueued', 'OsmExtractQueued'),
    analysisJobsOpen: counts(queueRow.analysisJobsOpen ?? queueRow.AnalysisJobsOpen),
    queued: counts(queueRow.queued ?? queueRow.Queued),
    claimed: num(queueRow, 'claimed', 'Claimed'),
    analysisRunsInFlight: num(queueRow, 'analysisRunsInFlight', 'AnalysisRunsInFlight'),
  };

  const mapKindStatistics = (row: Row): FleetKindStatistics => ({
    total: num(row, 'total', 'Total'),
    succeeded: num(row, 'succeeded', 'Succeeded'),
    failed: num(row, 'failed', 'Failed'),
  });
  const statsRow = (root.statistics ?? root.Statistics ?? {}) as Row;
  const statistics: FleetStatistics = {
    workersTotal: num(statsRow, 'workersTotal', 'WorkersTotal'),
    workersOnline: num(statsRow, 'workersOnline', 'WorkersOnline'),
    courseAnalysis: mapKindStatistics((statsRow.courseAnalysis ?? statsRow.CourseAnalysis ?? {}) as Row),
    flyover: mapKindStatistics((statsRow.flyover ?? statsRow.Flyover ?? {}) as Row),
    ml: mapKindStatistics((statsRow.ml ?? statsRow.Ml ?? {}) as Row),
    osmExtract: mapKindStatistics((statsRow.osmExtract ?? statsRow.OsmExtract ?? {}) as Row),
  };

  const activityRows = (root.recentActivity ?? root.RecentActivity) as Row[] | undefined;
  const activity: FleetActivityItem[] = Array.isArray(activityRows)
    ? activityRows
        .map((row) => {
          const kind = str(row, 'kind', 'Kind');
          const id = str(row, 'id', 'Id');
          const at = str(row, 'at', 'At');
          if (!isFleetKind(kind) || !id || !at) return null;
          return {
            kind,
            id,
            status: str(row, 'status', 'Status') ?? '',
            at,
            finishedAt: str(row, 'finishedAt', 'FinishedAt'),
            workerId: str(row, 'workerId', 'WorkerId'),
            summary: str(row, 'summary', 'Summary'),
            error: str(row, 'error', 'Error'),
            runId: str(row, 'runId', 'RunId'),
            detail: str(row, 'detail', 'Detail'),
          };
        })
        .filter((item): item is FleetActivityItem => item !== null)
    : [];

  return { workers, queues, statistics, activity };
};

/**
 * Polls the batch-worker fleet page's data. Kept to a plain interval rather than a
 * WebSocket subscription: this is a read-only operational view a developer opens
 * occasionally, not a live control surface, and geodata already answers the same shape to
 * the per-course/per-job panels from a query with no push side to reuse.
 */
export function useBatchFleet(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean,
  pollMs = 15000
): { state: FleetState; refresh: () => void } {
  const [state, setState] = useState<FleetState>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/batch/fleet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapFleetResponse(body) });
      } catch {
        if (!cancelled) {
          setState({ status: 'failed' });
        }
      } finally {
        if (!cancelled) {
          timer = setTimeout(load, pollMs);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [apiBaseUrl, token, enabled, pollMs, reloadKey]);

  return { state, refresh };
}

/**
 * Asks the worker running this job to stop it, so it can take the next one.
 *
 * Answers whether the *request* was filed, not whether the job died: geodata-service cannot
 * reach a worker, so it leaves the request in the exchange and the worker picks it up on its
 * next heartbeat. The card says "stopping", and the job's own line disappearing confirms it.
 *
 * What stopping costs depends on the job, which is why the button is labelled per kind: a
 * training run is killed on its own, while anything on an analyser costs that analyser a
 * restart — see `analyserRestartRequired`.
 */
export async function abortJob(
  apiBaseUrl: string,
  token: string | null | undefined,
  jobId: string,
  workerId: string
): Promise<{ ok: boolean; status: number | null }> {
  try {
    const response = await fetch(`${apiBaseUrl}/api/geodata/batch/jobs/${encodeURIComponent(jobId)}/abort`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // Which worker's copy: one job id can name work on several workers — a course-data sweep
      // is reported under the sweep's id — and geodata refuses to guess between them.
      body: JSON.stringify({ workerId }),
    });
    return { ok: response.ok, status: response.status };
  } catch {
    return { ok: false, status: null };
  }
}

/**
 * Whether stopping this job means restarting its analyser, rather than killing it on its own.
 *
 * Only a training run is a child process the ML runner can kill. Everything else is a call into
 * an analyser that has no cancellation point, so the only way to stop it is to end that
 * analyser's process — which takes down whatever else it was running. The button says so.
 */
export function analyserRestartRequired(kind: string): boolean {
  return kind !== 'ml';
}
